/**
 * ColumnAddFormBody — name/type/null/default/identity/generated form for
 * `add-column`. Identity is a bare-toggle: `identity: {}` synthesizes per-
 * engine to `IDENTITY` (mssql), `GENERATED BY DEFAULT AS IDENTITY` (pgsql/
 * oracle), `AUTO_INCREMENT` (mysql), `AUTOINCREMENT` (sqlite). Generated is a
 * `{ expr, virtual? }` spec — when the user fills the expression, identity +
 * default are mutually disabled and the column commits with `column.generated`
 * (DSE2 §3.A3).
 *
 * sqlite caller-contract: `INTEGER PRIMARY KEY` belongs in the type input.
 * Shared by `ColumnAddPopover` (Designer) + the inline picker popover.
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice A2 (identity), § Slice A3 (generated).
 */
import { useState } from 'react';

import { Button } from '../../../../../components/ui/button';
import type { ColumnDef } from '../../../../sql-resolver/types/ddlEdit';
import { commitAddColumnWithIdentity } from '../../edit/edits/setColumnIdentity';
import {
  isNonEmptyGeneratedSpec,
  persistenceModeToVirtual,
  validateGeneratedSpecForEngine,
  type GeneratedPersistenceMode,
} from '../../edit/edits/setColumnGenerated';
import {
  buildCollationSpecFromForm,
  isNonEmptyCollationSpec,
  validateCollationSpecForEngine,
} from '../../edit/edits/setColumnCollation';
import type { VqeDdlEditContext } from '../../edit/editContext';
import { ColumnAddBasicFields, type AddColumnNullableMode } from './ColumnAddBasicFields';
import { ColumnGeneratedFields } from './ColumnGeneratedFields';
import { ColumnCollationFields } from './ColumnCollationFields';
import { FormEngineChip } from './FormEngineChip';

export interface ColumnAddFormBodyProps {
  readonly editContext: VqeDdlEditContext;
  readonly testId: string;
  /** Optional `afterColumn` — passes through to the add-column op so a
   *  CREATE TABLE column-list insert lands after a chosen sibling. Only the
   *  Designer wires this; the inline picker leaves it undefined (append). */
  readonly afterColumn?: string;
  readonly onCommitted?: () => void;
  readonly onCancel?: () => void;
}

export function ColumnAddFormBody(props: ColumnAddFormBodyProps) {
  const { editContext, testId, afterColumn, onCommitted, onCancel } = props;
  const engine = editContext.service.engineId;

  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [nullableMode, setNullableMode] = useState<AddColumnNullableMode>('unspecified');
  const [defaultExpr, setDefaultExpr] = useState('');
  const [identity, setIdentity] = useState(false);
  const [generatedExpr, setGeneratedExpr] = useState('');
  const [generatedPersistence, setGeneratedPersistence] =
    useState<GeneratedPersistenceMode>(engine === 'oracle' ? 'virtual' : 'stored');
  const [collationName, setCollationName] = useState('');
  const [collationCharset, setCollationCharset] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const generatedActive = isNonEmptyGeneratedSpec({ expr: generatedExpr });

  function buildColumn(): ColumnDef | { error: string } {
    const trimmedName = name.trim();
    const trimmedType = type.trim();
    if (trimmedName === '') return { error: 'Name is required.' };
    if (trimmedType === '') return { error: 'Type is required.' };
    const draft: {
      name: string;
      typeRef: string;
      nullable?: boolean;
      defaultText?: string;
      identity?: ColumnDef['identity'];
      generated?: ColumnDef['generated'];
      collation?: ColumnDef['collation'];
    } = { name: trimmedName, typeRef: trimmedType };
    if (nullableMode === 'null') draft.nullable = true;
    if (nullableMode === 'not-null') draft.nullable = false;
    if (generatedActive) {
      const expr = generatedExpr.trim();
      const virtual = persistenceModeToVirtual(generatedPersistence);
      const spec = { expr, virtual };
      const validationError = validateGeneratedSpecForEngine(engine, spec);
      if (validationError !== null) return { error: validationError };
      draft.generated = spec;
    } else {
      const trimmedDefault = defaultExpr.trim();
      if (trimmedDefault !== '') draft.defaultText = trimmedDefault;
      if (identity) draft.identity = {};
    }
    const collationSpec = buildCollationSpecFromForm(collationName, collationCharset);
    if (isNonEmptyCollationSpec(collationSpec)) {
      const validationError = validateCollationSpecForEngine(engine, collationSpec);
      if (validationError !== null) return { error: validationError };
      draft.collation = collationSpec;
    }
    return draft as ColumnDef;
  }

  async function handleSubmit() {
    setError(null);
    const built = buildColumn();
    if ('error' in built) {
      setError(built.error);
      return;
    }
    setPending(true);
    try {
      await commitAddColumnWithIdentity(editContext, { column: built, afterColumn });
      onCommitted?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPending(false);
    }
  }

  const showSqliteIdentityHint = identity && !generatedActive && engine === 'sqlite';

  return (
    <div data-testid={testId} className="flex min-h-0 flex-col">
      {/* Scrollable body — the ONLY scrolling region, bounded by the popover's
          own max-height. `min-h-0` overrides the flex item's default
          `min-height: auto` (content-based floor) so it can actually shrink
          to fit and hand overflow to `overflow-y-auto` instead of pushing the
          footer below off-screen. `-mx-4 px-4` cancels the popover's own
          horizontal padding so the scrollbar track rides the true right edge
          (matches the setup-dialog scroll-body convention). */}
      <div className="-mx-4 min-h-0 flex-1 overflow-y-auto px-4">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold">Add column</div>
            <FormEngineChip engine={engine} />
          </div>

          <ColumnAddBasicFields
            engine={engine}
            testId={testId}
            name={name}
            setName={setName}
            type={type}
            setType={setType}
            nullableMode={nullableMode}
            setNullableMode={setNullableMode}
            defaultExpr={defaultExpr}
            setDefaultExpr={setDefaultExpr}
            defaultDisabled={generatedActive}
            identity={identity}
            setIdentity={setIdentity}
            identityDisabled={generatedActive}
            showSqliteIdentityHint={showSqliteIdentityHint}
          />

          <ColumnGeneratedFields
            engine={engine}
            testId={`${testId}-generated`}
            expr={generatedExpr}
            setExpr={setGeneratedExpr}
            persistence={generatedPersistence}
            setPersistence={setGeneratedPersistence}
          />

          <ColumnCollationFields
            engine={engine}
            testId={`${testId}-collation`}
            name={collationName}
            setName={setCollationName}
            charset={collationCharset}
            setCharset={setCollationCharset}
          />

          {error ? (
            <div
              className="rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-xs text-destructive"
              data-testid={`${testId}-error`}
            >
              {error}
            </div>
          ) : null}
        </div>
      </div>

      {/* Footer sits OUTSIDE the scrollable region as a normal flex sibling,
          so it always renders in full below the last field — never sliced by
          or overlapping scrolled content. */}
      <div className="-mx-4 -mb-4 flex items-center justify-end gap-2 border-t bg-popover px-4 py-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onCancel?.()}
          data-testid={`${testId}-cancel`}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={pending}
          onClick={() => void handleSubmit()}
          data-testid={`${testId}-submit`}
        >
          {pending ? 'Adding…' : 'Add'}
        </Button>
      </div>
    </div>
  );
}
